import _ from 'lodash';
import moment from 'moment';

export const getSemesterDisplayName = (semester, html) => {
  // TODO admin section related only - put into different file in the future
  if (!_.isObject(semester)) return '';

  const prefix = _.isEmpty(semester.name) ? `${semester.major}.${semester.minor}` : semester.name;

  if (semester.major <= 0) {
    return html ? '<strong class="name">Global</strong><br><span class="timeframe">-</span>' : prefix;
  }

  const start = moment(semester.date ? semester.date.starts_at : semester.starts_at);
  const end = moment(semester.date ? semester.date.ends_at : semester.ends_at);
  const sameYear = start.isSame(end, 'year');

  if (html) {
    const year = start.format('YYYY');

    return `${`<strong>${prefix}</strong><br>` +
            '<span>'}${sameYear ? year : `${year}/${end.format('YYYY')}`}</span>`;
  }

  const year = start.format('YY');
  const suffix = sameYear ? `SS ${year}` : `WS ${year}/${end.format('YY')}`;

  return `${prefix} (${suffix})`;
};

/**
 * Builds a display name from the given user object.
 *
 * If the user is a string, this will simply return that string.
 * If the user is an object and has a value for 'user_name', that will be returned.
 * Otherwise its first and last name will be joined together.
 *
 * @param user
 * @returns {string}
 */
export const displayNameUser = (user) => {
  if (typeof user === 'string') return user;

  if (user.user_name) {
    return user.user_name;
  }

  const firstName = user.nickname || user.first_name;

  return `${firstName} ${user.last_name}`;
};

/**
 * Converts the given array of users to a joined string of their display names,
 * each seperated by a comma.
 *
 * See the displayNameUser function for more information on the format.
 *
 * @param names
 * @returns {*}
 */
export const implodeNames = names => _.map(names, displayNameUser).join(', ');

/**
 * Returns the first and last name of the user, separated by comma, last in front.
 *
 * @param user
 * @returns {string}
 */
export const listNameUser = user => `${user.last_name}, ${user.nickname || user.first_name}`;

/**
 * Helper method to quickly get the name of a student by accessing their user object.
 *
 * @param student
 * @returns {string}
 */
export const listNameStudent = student => listNameUser(student.user);

/**
 * Returns the bootstrap color for the given role id.
 * If the given role is an object, it will use its 'id' property instead.
 *
 * @param role
 * @param isText
 * @returns {*}
 */
export const roleColor = (role, isText) => {
  const id = _.isObject(role) ? role.id : role;

  if (id < 4) return 'danger'; // Management
  if (id === 4) return 'primary'; // Teacher
  if (id === 6) return 'warning'; // Aborted
  if (id === 7) return isText ? 'muted' : 'secondary'; // Prospect
  if (id === 9) return isText ? 'muted' : 'default'; // Graduate (inactive)

  return 'info';
};

/**
 * Returns the bootstrap label class for the given role.
 *
 * @param role
 * @param isText
 * @returns {string}
 */
export const roleLabel = (role, isText = false) => `label label-${roleColor(role, isText)}`;

/**
 * Returns a string for the given average.
 * If the excuse flag is set, values below zero will return an 'E'.
 *
 * @param average
 * @param excused
 */
export const averageStr = (average, excused = true) => {
  if (average < 0) return excused ? 'E' : '-';
  return `${average}%`;
};

/**
 * Calculates the average of a list of given grades.
 * Ignores any invalid grades (value below zero).
 *
 * @param grades
 * @returns {number}
 */
export const averageOf = (grades) => {
  if (grades.length === 0) {
    return -1;
  }

  if (!_.isArray(grades) || grades[0].grade !== undefined) {
    grades = _.map(grades, 'grade');
  }

  const values = _.filter(grades, grade => (grade >= 0));
  if (values.length === 0) return -1;

  return Math.round(_.sumBy(values, grade => Number(grade)) / values.length);
};

export const averageOfCourse = (course) => {
  const values = _.reduce(course.exams, (map, { grades }) => {
    _.each(grades, ({ grade, student_id: student }) => {
      if (!grade && grade !== 0) return;

      const list = map[student] || (map[student] = []);
      list.push(grade);
    });

    return map;
  }, {});

  return _.mapValues(values, list => averageOf(list));
};

/**
 * Simple function to convert line breaks to HTML-brs.
 *
 * @param text
 */
export const nl2br = text => `${text}`.replace(/([^>\r\n]?)(\r\n|\n\r|\r|\n)/g, '$1<br>$2');

/**
 * Indicates if the given role is a student role.
 *
 * @param id
 * @return {boolean}
 */
export const isStudentRole = ({ id }) => id === 5;

export default function install(Vue) {
  Vue.mixin({
    methods: {
      getSemesterDisplayName,
      implodeNames,
      displayNameUser,
      listNameUser,
      listNameStudent,
      roleColor,
      roleLabel,
      isStudentRole,
      averageStr,
      averageOf,
      averageOfCourse,
      nl2br,
    },
  });
}



// WEBPACK FOOTER //
// ./resources/assets/js/vue-extensions/presenters.js